import { useEffect, useRef, useState } from 'react';

import { RunEvent } from 'types';

interface UseRunStreamResult {
  events: RunEvent[];
  isConnected: boolean;
  isFinished: boolean;
}

/**
 * Subscribe to the SSE event stream for a single accountctl run. The server
 * writes every event as `event: message`, so the default `'message'` listener
 * on `EventSource` is sufficient.
 *
 * The hook owns the canonical event list: each frame is parsed as a typed
 * `RunEvent` and appended in order. Components derive the step timeline,
 * banners, and finished-state from this array.
 *
 * Lifecycle:
 *   - Mount → open `EventSource` against `/api/internal/accountctl/runs/:id/events`.
 *   - On a `run.finished` frame, flip `isFinished` AND close the EventSource.
 *     Native EventSource treats any server-initiated close as transient and
 *     auto-reconnects; without an explicit `.close()` here the client would
 *     reconnect endlessly, replay the persisted backlog each cycle, and the
 *     timeline would fill up with duplicates of every event.
 *   - Unmount → close the source.
 *
 * Before a run finishes, `EventSource` reconnects automatically on transient
 * drops; the server replays persisted events from the doc before attaching
 * to the live broker, so the timeline rebuilds across refreshes and short
 * network blips.
 */
export const useRunStream = (runId: string | undefined): UseRunStreamResult => {
  const [events, setEvents] = useState<RunEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  // Track the runId the current EventSource is bound to so navigating from
  // /runs/A to /runs/B tears down the previous source and clears state.
  const lastRunIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!runId) return;

    // New run id — start with a fresh event list.
    if (lastRunIdRef.current !== runId) {
      setEvents([]);
      setIsFinished(false);
      lastRunIdRef.current = runId;
    }

    const source = new EventSource(`/api/internal/accountctl/runs/${runId}/events`);

    const handleOpen = (): void => {
      setIsConnected(true);
    };

    const handleMessage = (evt: MessageEvent<string>): void => {
      try {
        const payload = JSON.parse(evt.data) as RunEvent;
        setEvents((prev) => [...prev, payload]);
        if (payload.type === 'run.finished') {
          setIsFinished(true);
          // Stop the browser from auto-reconnecting and replaying the backlog.
          source.close();
          setIsConnected(false);
        }
      } catch {
        // Ignore malformed frames — they shouldn't happen in practice,
        // but a broken parse must not poison the whole subscription.
      }
    };

    const handleError = (): void => {
      // The browser will auto-reconnect for transient errors; only flip the
      // connected flag so the UI can show a subtle indicator if it wants to.
      setIsConnected(false);
    };

    source.addEventListener('open', handleOpen);
    source.addEventListener('message', handleMessage);
    source.addEventListener('error', handleError);

    return (): void => {
      source.removeEventListener('open', handleOpen);
      source.removeEventListener('message', handleMessage);
      source.removeEventListener('error', handleError);
      source.close();
      setIsConnected(false);
    };
  }, [runId]);

  return { events, isConnected, isFinished };
};
